/**
 * Campaign flag ceiling — campaign-level Brand Safety preferences constrain the per-influencer config.
 *
 * A campaign's preferences (`/campaign/getBrandSafetyPreference`) are a CEILING: a flag/severity the
 * campaign turned off is off everywhere, and the influencer-level Filters drawer must not be able to
 * turn it back on. These helpers are pure so the rule can live in one place (the slice reducers that
 * write `filters`) rather than being re-implemented per surface.
 *
 * Both levels use the campaign-creation map shape `{ [Flag]: { severe, moderate, low, minor } }`, but
 * the campaign preference is also stored as a LEGACY FLAT BOOLEAN map (`{ AlcoholFlag: true, ... }`)
 * — `normalizePreferences` bridges both (flat `true` ⇒ all four severities true), so everything here
 * runs its inputs through it first.
 */

import {
  BRAND_SAFETY_SEVERITY_FLAG_KEYS,
  BRAND_SAFETY_SEVERITY_KEYS,
} from '../../Persona/BrandSafety/brandSafetyConsts';
import { normalizePreferences } from '../../Persona/BrandSafety/brandSafetySeverity';

/** Build a full 12-flag × 4-severity map from a per-leaf predicate. */
function buildPreferences(leafValue) {
  return Object.fromEntries(
    BRAND_SAFETY_SEVERITY_FLAG_KEYS.map((flagKey) => [
      flagKey,
      Object.fromEntries(
        BRAND_SAFETY_SEVERITY_KEYS.map((severityKey) => [
          severityKey,
          leafValue(flagKey, severityKey),
        ])
      ),
    ])
  );
}

/**
 * Constrain a preferences map to a ceiling: a leaf survives only if BOTH allow it. A null/absent
 * ceiling means "no campaign context" and leaves the preferences untouched (fail open), which is what
 * a deep-linked report with no campaignId gets.
 *
 * @param {object} prefs   the per-influencer (or drawer) preferences map
 * @param {object|null} ceiling  the campaign preferences map, or null when unknown
 * @returns {object} a fully normalized map, clamped to the ceiling
 */
export function clampPreferencesToCeiling(prefs, ceiling) {
  const normalized = normalizePreferences(prefs);
  if (!ceiling) return normalized;
  const limit = normalizePreferences(ceiling);

  return buildPreferences(
    (flagKey, severityKey) =>
      Boolean(normalized[flagKey][severityKey]) && Boolean(limit[flagKey][severityKey])
  );
}

/**
 * The leaves the campaign has turned off — `{ [Flag]: { [severity]: true } }` where `true` means
 * LOCKED (rendered as a disabled toggle with an explanatory tooltip). Null ceiling ⇒ null (nothing
 * locked), so the shared severity panel keeps its current behaviour wherever no campaign applies.
 *
 * @param {object|null} ceiling
 * @returns {object|null}
 */
export function deriveLockedSeverities(ceiling) {
  if (!ceiling) return null;
  const limit = normalizePreferences(ceiling);

  return buildPreferences((flagKey, severityKey) => limit[flagKey][severityKey] === false);
}

/**
 * Fold the panel's emitted map back into the unclamped base.
 *
 * The drawer renders the CLAMPED map, so what it emits has every campaign-disabled leaf already off.
 * Writing that straight to the base would bake a view-time constraint into the user's stored intent —
 * and the intent could never be recovered if the campaign later re-enables the flag. Leaves the
 * ceiling permits come from `next` (the user really did choose them); leaves it blocks keep whatever
 * the base already held, since the user could not have changed a locked toggle.
 *
 * @param {object} base     the current unclamped preferences
 * @param {object} next     the map the severity panel emitted
 * @param {object|null} ceiling
 * @returns {object} the new unclamped base
 */
export function mergeUnderCeiling(base, next, ceiling) {
  const normalizedNext = normalizePreferences(next);
  if (!ceiling) return normalizedNext;
  const normalizedBase = normalizePreferences(base);
  const limit = normalizePreferences(ceiling);

  return buildPreferences((flagKey, severityKey) =>
    limit[flagKey][severityKey]
      ? Boolean(normalizedNext[flagKey][severityKey])
      : Boolean(normalizedBase[flagKey][severityKey])
  );
}

/**
 * Deep equality over the canonical 12 flags × 4 severities. Used to tell "the user narrowed the view"
 * from "the campaign ceiling is simply not all-on", so a temporary-filter banner only shows for an
 * actual user edit.
 */
export function preferencesEqual(a, b) {
  const left = normalizePreferences(a);
  const right = normalizePreferences(b);
  return BRAND_SAFETY_SEVERITY_FLAG_KEYS.every((flagKey) =>
    BRAND_SAFETY_SEVERITY_KEYS.every(
      (severityKey) => Boolean(left[flagKey][severityKey]) === Boolean(right[flagKey][severityKey])
    )
  );
}
